import { CheckIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { FC } from "react";
import {
  EsimStage,
  ESIM_STAGE_ORDER,
  getEsimStageColor,
  getEsimStageDescription,
  getEsimStageLabel,
  isHappyPathStage
} from "../../../utils/esim/esimStatus";

// Problem states that still happened ON the install rail: show how far the eSIM got
// and flag the step where it is stuck, instead of dropping it off the rail entirely.
// `reached` = index of the last completed step; `blocked` = index of the stuck step.
const STUCK_ON_RAIL: Record<string, { reached: number; blocked: number }> = {
  // Enabled on the carrier side (step 4) but never reached the network (step 5).
  NeverConnected: { reached: 3, blocked: 4 }
};

type StepState = "done" | "current" | "blocked" | "pending";

interface StageStepperProps {
  stage: string;
}

const StageStepper: FC<StageStepperProps> = ({ stage }) => {
  const stuck = STUCK_ON_RAIL[stage];
  const onRail = isHappyPathStage(stage) || !!stuck;

  // Genuinely off-flow states (Deleted, Disabled, Unknown) render as a single badge.
  if (!onRail) {
    return (
      <div className="flex flex-col gap-2">
        <span
          className={`w-fit px-3 py-1 rounded-full text-sm font-medium ${getEsimStageColor(
            stage
          )}`}
        >
          {getEsimStageLabel(stage)}
        </span>
        <p className="text-sm text-gray-600">{getEsimStageDescription(stage)}</p>
      </div>
    );
  }

  const currentIndex = stuck ? -1 : ESIM_STAGE_ORDER.indexOf(stage as EsimStage);

  const stepState = (index: number): StepState => {
    if (stuck) {
      if (index <= stuck.reached) return "done";
      if (index === stuck.blocked) return "blocked";
      return "pending";
    }
    if (index < currentIndex) return "done";
    if (index === currentIndex) return "current";
    return "pending";
  };

  const circleClass = (state: StepState): string => {
    switch (state) {
      case "done":
        return "bg-green text-white";
      case "current":
        return "bg-primary text-white ring-4 ring-primary/20";
      case "blocked":
        return "bg-red text-white ring-4 ring-red/20";
      default:
        return "bg-gray-200 text-gray-500";
    }
  };

  return (
    <div>
      <div className="flex items-start">
        {ESIM_STAGE_ORDER.map((step, index) => {
          const state = stepState(index);
          const prevDone = index > 0 && stepState(index - 1) === "done";

          return (
            <div
              key={step}
              className="flex-1 flex flex-col items-center relative"
            >
              {index > 0 && (
                <div
                  className={`absolute top-4 right-1/2 w-full h-0.5 ${
                    prevDone ? "bg-green" : "bg-gray-200"
                  }`}
                />
              )}
              <div
                className={`relative z-10 flex h-8 w-8 items-center justify-center
                  rounded-full text-xs font-semibold ${circleClass(state)}`}
              >
                {state === "done" ? (
                  <CheckIcon className="w-4 h-4" />
                ) : state === "blocked" ? (
                  <XMarkIcon className="w-4 h-4" />
                ) : (
                  index + 1
                )}
              </div>
              <span
                className={`mt-1 text-[11px] text-center w-20 leading-tight ${
                  state === "current" || state === "blocked"
                    ? "font-semibold text-gray-900"
                    : "text-gray-500"
                }`}
              >
                {getEsimStageLabel(step)}
              </span>
            </div>
          );
        })}
      </div>
      <p className="text-sm text-gray-600 mt-3">
        {getEsimStageDescription(stage)}
      </p>
    </div>
  );
};

export default StageStepper;
